import {
    Box,
    Button,
    LinearProgress,
    Paper,
    Typography
} from "@mui/material";
import {
    useMemo,
    useState
} from "react";
import {
    useQuery,
    useQueryClient
} from "react-query";

import { ContactCard } from "./ContactCard";
import { ContactFormDialog } from "./ContactFormDialog";
import { ContactListSortMenu } from "./ContactListSortMenu";
import { SortDirection } from "../../lib/sorting/SortDirection";
import { graphQLContactDAO } from "../../lib/contact/dao/GraphQLContactDAO";
import { searchCriteriaState } from "../../lib/search/SearchCriteriaState";
import { useRecoilValue } from "recoil";

export const ContactList = () => {
    const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState<boolean>(false);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);
    const searchCriteria = useRecoilValue(searchCriteriaState);
    const { data: contacts, status } = useQuery({
        queryKey: ["contacts"],
        queryFn: async () => {
            return await graphQLContactDAO.list();
        }
    });
    const queryClient = useQueryClient();

    const filteredContacts = useMemo(() => {
        return contacts?.filter((contact) => {
            if (!searchCriteria) {
                return true;
            }
            const contactValuesString = `${contact.name || ""} ${contact.email || ""} ${contact.phoneNumber || ""}`;
            return contactValuesString.toLowerCase().includes(searchCriteria.keywords.toLowerCase());
        });
    }, [contacts, searchCriteria]);

    const sortedContacts = useMemo(() => {
        return filteredContacts?.sort((a, b) => {
            if (!a.name || !b.name) {
                return 0;
            }
            if (sortDirection === SortDirection.ASCENDING) {
                return a.name.localeCompare(b.name);
            }
            return b.name.localeCompare(a.name);
        });
    }, [filteredContacts, sortDirection]);

    if (status === "loading") {
        return <LinearProgress />;
    }

    if (status === "error") {
        return <Box p={2}>Failed to load contacts</Box>;
    }

    return (
        <>
            <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Button variant="contained" color="primary" onClick={() => setIsAddContactDialogOpen(true)}>
                        Add Contact
                    </Button>
                    <ContactListSortMenu
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                    />
                </Box>
                <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
                    {sortedContacts && sortedContacts.length > 0 ? sortedContacts?.map((contact) => (
                        <ContactCard
                            key={contact.id}
                            contact={contact}
                        />
                    )) : (
                        <Paper sx={{ padding: 2, gridColumn: "1 / -1" }}>
                            <Typography variant="h6">No contacts found</Typography>
                        </Paper>
                    )}
                </Box>
            </Box>
            <ContactFormDialog
                isOpen={isAddContactDialogOpen}
                setIsOpen={setIsAddContactDialogOpen}
                type="add"
                onSubmit={async (formData) => {
                    await graphQLContactDAO.create({
                        name: formData.name,
                        email: formData.email,
                        phoneNumber: formData.phoneNumber
                    });
                    await queryClient.invalidateQueries("contacts");
                    setIsAddContactDialogOpen(false);
                }}
            />
        </>
    );
};